import { useState, useEffect } from "react";
import { PageTitle } from "../../components";
import { Table, Space, Form, Input, Select, Button, DatePicker, Tag, message } from "antd";
import type { ColumnsType } from "antd/lib/table";
import dayjs from "dayjs";
import { auditLogsApi, type AuditLog } from "../../_helpers/apiService";
import { CURRENT_CLUB_ID } from "../../_helpers/constants";

const { RangePicker } = DatePicker;
const { Option } = Select;

function AuditLogs() {
  const [searchForm] = Form.useForm();
  const [data, setData] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState<any>({});

  useEffect(() => {
    fetchAuditLogs(currentPage, pageSize, filters);
  }, [currentPage, pageSize]);

  const fetchAuditLogs = async (page: number, size: number, searchFilters: any = {}) => {
    setLoading(true);
    try {
      const skip = (page - 1) * size;
      const params: any = {
        club_id: CURRENT_CLUB_ID,
        skip,
        limit: size,
        ...searchFilters,
      };

      const response = await auditLogsApi.getAll(params);
      setData(response.data.items);
      setTotal(response.data.total);
    } catch (error: any) {
      console.error("Fetch audit logs error:", error);
      message.error("Аудит логийн мэдээлэл татахад алдаа гарлаа");
    } finally {
      setLoading(false);
    }
  };

  const onFinish = (values: any) => {
    const searchFilters: any = {};

    if (values.entity) {
      searchFilters.entity = values.entity;
    }

    if (values.action) {
      searchFilters.action = values.action;
    }

    if (values.user_id) {
      searchFilters.user_id = parseInt(values.user_id);
    }

    if (values.dateRange && values.dateRange.length === 2) {
      searchFilters.start_date = values.dateRange[0].format('YYYY-MM-DD');
      searchFilters.end_date = values.dateRange[1].format('YYYY-MM-DD');
    }

    setFilters(searchFilters);
    setCurrentPage(1);
    fetchAuditLogs(1, pageSize, searchFilters);
  };

  const clearSearchForm = () => {
    searchForm.resetFields();
    setFilters({});
    setCurrentPage(1);
    fetchAuditLogs(1, pageSize, {});
  };

  const handleTableChange = (pagination: any) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case 'create':
        return 'green';
      case 'update':
        return 'blue';
      case 'delete':
        return 'red';
      default:
        return 'default';
    }
  };

  const getActionText = (action: string) => {
    switch (action) {
      case 'create':
        return 'Үүсгэх';
      case 'update':
        return 'Засах';
      case 'delete':
        return 'Устгах';
      default:
        return action;
    }
  };

  const getEntityText = (entity: string) => {
    const entityMap: any = {
      user: 'Хэрэглэгч',
      team: 'Баг',
      rank: 'Цол/Зэрэг',
      competition: 'Тэмцээн',
      news: 'Мэдээ',
      referee: 'Шүүгч',
      leader: 'Ахлагч',
    };
    return entityMap[entity] || entity;
  };

  const columns: ColumnsType<AuditLog> = [
    {
      title: "№",
      dataIndex: "id",
      key: "id",
      width: 80,
      render: (text, row, index) => (currentPage - 1) * pageSize + index + 1,
    },
    {
      title: "Огноо",
      dataIndex: "timestamp",
      key: "timestamp",
      width: 180,
      render: (value) => dayjs(value).format("YYYY-MM-DD HH:mm:ss"),
      sorter: (a, b) => dayjs(a.timestamp).unix() - dayjs(b.timestamp).unix(),
    },
    {
      title: "Хэрэглэгч",
      dataIndex: "user_email",
      key: "user_email",
      width: 200,
    },
    {
      title: "Үйлдэл",
      dataIndex: "action",
      key: "action",
      width: 100,
      render: (action) => (
        <Tag color={getActionColor(action)}>{getActionText(action)}</Tag>
      ),
    },
    {
      title: "Обьект",
      dataIndex: "entity",
      key: "entity",
      width: 150,
      render: (entity) => getEntityText(entity),
    },
    {
      title: "Нэр",
      dataIndex: "entity_name",
      key: "entity_name",
      width: 200,
    },
    {
      title: "Өөрчлөлт",
      dataIndex: "changes",
      key: "changes",
      width: 300,
      render: (changes) => {
        if (!changes) return '-';
        try {
          const parsed = JSON.parse(changes);
          return (
            <pre style={{
              margin: 0,
              fontSize: '12px',
              maxHeight: '100px',
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
              wordWrap: 'break-word'
            }}>
              {JSON.stringify(parsed, null, 2)}
            </pre>
          );
        } catch (e) {
          return changes;
        }
      },
    },
  ];

  return (
    <>
      <PageTitle title="Аудит Лог" description="Системийн үйлдлийн түүх" />

      <div className="row">
        <div className="col-lg-12">
          <div className="card">
            <div className="card-body">
              <Form
                form={searchForm}
                name="search-form"
                onFinish={onFinish}
                layout="inline"
                style={{ marginBottom: 20 }}
              >
                <Form.Item name="entity" style={{ marginBottom: 10 }}>
                  <Select
                    placeholder="Обьект сонгох"
                    allowClear
                    style={{ width: 200 }}
                  >
                    <Option value="user">Хэрэглэгч</Option>
                    <Option value="team">Баг</Option>
                    <Option value="rank">Цол/Зэрэг</Option>
                    <Option value="competition">Тэмцээн</Option>
                    <Option value="news">Мэдээ</Option>
                    <Option value="referee">Шүүгч</Option>
                    <Option value="leader">Ахлагч</Option>
                  </Select>
                </Form.Item>

                <Form.Item name="action" style={{ marginBottom: 10 }}>
                  <Select
                    placeholder="Үйлдэл сонгох"
                    allowClear
                    style={{ width: 150 }}
                  >
                    <Option value="create">Үүсгэх</Option>
                    <Option value="update">Засах</Option>
                    <Option value="delete">Устгах</Option>
                  </Select>
                </Form.Item>

                <Form.Item name="user_id" style={{ marginBottom: 10 }}>
                  <Input
                    placeholder="Хэрэглэгчийн ID"
                    style={{ width: 150 }}
                  />
                </Form.Item>

                <Form.Item name="dateRange" style={{ marginBottom: 10 }}>
                  <RangePicker
                    format="YYYY-MM-DD"
                    placeholder={['Эхлэх огноо', 'Дуусах огноо']}
                  />
                </Form.Item>

                <Form.Item style={{ marginBottom: 10 }}>
                  <Button type="primary" htmlType="submit">
                    Хайх
                  </Button>
                </Form.Item>

                <Form.Item style={{ marginBottom: 10 }}>
                  <Button onClick={clearSearchForm}>Цэвэрлэх</Button>
                </Form.Item>
              </Form>

              <Table
                columns={columns}
                dataSource={data}
                loading={loading}
                rowKey="id"
                pagination={{
                  current: currentPage,
                  pageSize: pageSize,
                  total: total,
                  showSizeChanger: true,
                  showTotal: (total) => `Нийт ${total} бичлэг`,
                  pageSizeOptions: ['10', '20', '50', '100'],
                }}
                onChange={handleTableChange}
                scroll={{ x: 1400 }}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default AuditLogs;
